/**
 * Title/Escrow Conditions Section
 *
 * Professional flat list of conditions for title/escrow companies.
 * No role grouping — conditions are listed directly under each timing section.
 * Title/escrow professionals understand PTD/PTF/AC industry terms.
 */

import { CheckCircle2, Circle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { ConditionCard } from './ConditionCardShared';
import type { ConditionsSectionProps } from './ConditionCardShared';

export const TitleEscrowConditionsSection = ({
  title,
  conditions,
  templates,
  uploads,
  notes,
  onDownloadTemplate,
  onUploadClick,
  borrowerPairs
}: ConditionsSectionProps) => {
  const openCount = conditions.filter(c => c.statusOpen).length;
  const allCleared = openCount === 0;

  return (
    <div className="space-y-3">
      {/* Section Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {allCleared ? (
            <CheckCircle2 className="h-5 w-5 text-green-500" />
          ) : (
            <Circle className="h-5 w-5 text-amber-500" />
          )}
          <h3 className="font-semibold text-gray-900">{title}</h3>
        </div>
        <Badge
          variant="secondary"
          className={allCleared ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}
        >
          {allCleared ? 'All Cleared' : `${openCount} Open`}
        </Badge>
      </div>

      {/* Flat list of conditions — no role grouping */}
      <div className="space-y-2 pl-7">
        {conditions.map((condition) => {
          const conditionKey = condition.externalId || condition.id || condition.internalId || '';
          return (
            <ConditionCard
              key={condition.id}
              condition={condition}
              template={templates[condition.externalId]}
              uploads={uploads.filter(u => u.conditionExternalId === condition.externalId)}
              note={notes?.[conditionKey]}
              onDownloadTemplate={onDownloadTemplate}
              onUploadClick={onUploadClick}
              borrowerPairs={borrowerPairs}
            />
          );
        })}
      </div>
    </div>
  );
};
